package org.maplibre.compose.layers

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.value.ColorValue
import org.maplibre.compose.expressions.value.DpValue
import org.maplibre.compose.expressions.value.ImageValue
import org.maplibre.compose.interaction.ClickEvent
import org.maplibre.compose.interaction.ClickResult
import org.maplibre.compose.location.LocationState
import org.maplibre.compose.location.mostAccurateBearingMeasurement
import org.maplibre.compose.location.rememberDefaultHeadingProvider
import org.maplibre.compose.location.rememberLocationState
import org.maplibre.compose.style.TransitionOptions
import org.maplibre.compose.util.MaplibreComposable
import org.maplibre.spatialk.geojson.Position
import org.maplibre.spatialk.units.Bearing
import org.maplibre.spatialk.units.Length
import org.maplibre.spatialk.units.Rotation
import org.maplibre.spatialk.units.extensions.inDegrees
import org.maplibre.spatialk.units.extensions.inMeters

/**
 * Displays the latest location and the most accurate course or device heading from [locationState].
 *
 * By default, remembers lifecycle-aware location and heading providers. Permission is never
 * requested automatically. Pass a hoisted [rememberLocationState] to request permission, inspect
 * provider status, share measurements with camera tracking, or supply a custom provider.
 *
 * Retained measurements remain visible when tracking stops. The layer does not apply stale styling
 * or an accuracy threshold. Use the value overload for other display policies. Other parameters
 * behave as in the [Position] overload.
 */
@Composable
@MaplibreComposable
public fun LocationIndicatorLayer(
  id: String,
  locationState: LocationState =
    rememberLocationState(headingProvider = rememberDefaultHeadingProvider()),
  minZoom: Float = 0f,
  maxZoom: Float = 24f,
  visible: Boolean = true,
  bearingAccuracyRadius: Expression<DpValue> = const(48.dp),
  bearingAccuracyColor: Expression<ColorValue> = const(Color.Blue.copy(alpha = 0.35f)),
  accuracyRadiusColor: Color = Color.Blue.copy(alpha = 0.15f),
  accuracyRadiusBorderColor: Color = Color.Blue,
  topImage: Expression<ImageValue?>? = LocationIndicatorDefaults.topImage(),
  bearingImage: Expression<ImageValue?>? = LocationIndicatorDefaults.bearingImage(),
  shadowImage: Expression<ImageValue?>? = LocationIndicatorDefaults.shadowImage(),
  topImageSize: Float = 1f,
  bearingImageSize: Float = 1f,
  shadowImageSize: Float = 1f,
  imageTiltDisplacement: Float = 0f,
  perspectiveCompensation: Float = 0.85f,
  locationTransition: TransitionOptions = TransitionOptions(),
  bearingTransition: TransitionOptions = locationTransition,
  accuracyRadiusTransition: TransitionOptions = locationTransition,
  bearingAccuracyTransition: TransitionOptions = bearingTransition,
  onClick: (ClickEvent.() -> ClickResult)? = null,
  onLongClick: (ClickEvent.() -> ClickResult)? = null,
  onDoubleClick: (ClickEvent.() -> ClickResult)? = null,
  hitPadding: Dp = 0.dp,
) {
  val measurement = locationState.mostAccurateBearingMeasurement()
  LocationIndicatorLayer(
    id = id,
    location = locationState.lastLocation?.position,
    bearing = measurement?.bearing,
    bearingAccuracy = measurement?.accuracy,
    accuracyRadius = locationState.lastLocation?.horizontalAccuracy,
    minZoom = minZoom,
    maxZoom = maxZoom,
    visible = visible,
    bearingAccuracyRadius = bearingAccuracyRadius,
    bearingAccuracyColor = bearingAccuracyColor,
    accuracyRadiusColor = accuracyRadiusColor,
    accuracyRadiusBorderColor = accuracyRadiusBorderColor,
    topImage = topImage,
    bearingImage = bearingImage,
    shadowImage = shadowImage,
    topImageSize = topImageSize,
    bearingImageSize = bearingImageSize,
    shadowImageSize = shadowImageSize,
    imageTiltDisplacement = imageTiltDisplacement,
    perspectiveCompensation = perspectiveCompensation,
    locationTransition = locationTransition,
    bearingTransition = bearingTransition,
    accuracyRadiusTransition = accuracyRadiusTransition,
    bearingAccuracyTransition = bearingAccuracyTransition,
    onClick = onClick,
    onLongClick = onLongClick,
    onDoubleClick = onDoubleClick,
    hitPadding = hitPadding,
  )
}

/**
 * Draws a location dot, an optional bearing image, a horizontal accuracy circle, and a bearing
 * accuracy sector.
 *
 * The first location appears immediately; later measurements animate with the supplied transition
 * options. Bearing and longitude changes take the shortest path across north and the antimeridian.
 * A null [location] removes the indicator and resets its animation history.
 *
 * Click handlers target the top and bearing image bounds, including transparent margins, but not
 * the shadow, accuracy circle, or bearing accuracy sector. Each gesture invokes its handler at most
 * once, even when the images overlap. Handlers run with the [ClickEvent] as their receiver, so they
 * can read where and how the click happened. Return [ClickResult.Pass] to continue to layers below
 * or [ClickResult.Consume] to stop dispatch.
 *
 * @param id Unique layer ID.
 * @param location Position of the indicator, or null to hide it. Altitude is not rendered.
 * @param bearing Rotation of all three images clockwise from north. Null hides [bearingImage] and
 *   resets the other images to zero rotation. Its first available value appears immediately.
 * @param accuracyRadius Horizontal error radius in meters, or null to hide the circle.
 * @param bearingAccuracy Angular error on either side of [bearing]. For example, 15 degrees draws a
 *   30-degree sector. Values at least 180 degrees draw a full circle; zero hides it. Must be finite
 *   and nonnegative. Null accuracy or bearing hides the sector immediately; its first complete
 *   measurement appears immediately.
 * @param bearingAccuracyRadius Visual sector radius in dp before perspective compensation,
 *   independent of [accuracyRadius]. Zero hides it. Supports constant and zoom expressions.
 * @param bearingAccuracyColor Sector color at the center, fading smoothly to transparent at the
 *   outer edge. Supports constant and zoom expressions. Drawn below all images, without their pitch
 *   displacement. The sector does not affect hit testing.
 * @param bearingAccuracyTransition Timing for changes to sector angle, radius, and color. Defaults
 *   to [bearingTransition]. Zoom expression evaluation follows the camera without a transition.
 * @param minZoom Minimum visible zoom, inclusive.
 * @param maxZoom Maximum visible zoom, exclusive.
 * @param visible Whether to draw the indicator.
 * @param accuracyRadiusColor Fill color of the horizontal accuracy circle.
 * @param accuracyRadiusBorderColor Border color of the horizontal accuracy circle.
 * @param topImage Top image, normally the dot. Null omits it. Images must resolve to constant
 *   names.
 * @param bearingImage Image below [topImage]. Null omits it.
 * @param shadowImage Image below the other images. Null omits it.
 * @param topImageSize Scale of [topImage].
 * @param bearingImageSize Scale of [bearingImage].
 * @param shadowImageSize Scale of [shadowImage].
 * @param imageTiltDisplacement Image displacement when pitched, in pixels.
 * @param perspectiveCompensation Perspective compensation: zero follows map perspective; one
 *   preserves screen size.
 * @param locationTransition Duration and delay for position changes.
 * @param bearingTransition Bearing transition timing, defaulting to [locationTransition].
 * @param accuracyRadiusTransition Accuracy transition timing, defaulting to [locationTransition].
 * @param onClick Called when an indicator image is clicked.
 * @param onLongClick Called for a touch long press or secondary mouse click.
 * @param onDoubleClick Called for a double tap or double click.
 * @param hitPadding Expands tap queries to a square of this radius in dp; zero uses a point.
 */
@Composable
@MaplibreComposable
public fun LocationIndicatorLayer(
  id: String,
  location: Position?,
  bearing: Bearing? = null,
  accuracyRadius: Length? = null,
  bearingAccuracy: Rotation? = null,
  minZoom: Float = 0f,
  maxZoom: Float = 24f,
  visible: Boolean = true,
  bearingAccuracyRadius: Expression<DpValue> = const(48.dp),
  bearingAccuracyColor: Expression<ColorValue> = const(Color.Blue.copy(alpha = 0.35f)),
  accuracyRadiusColor: Color = Color.Blue.copy(alpha = 0.15f),
  accuracyRadiusBorderColor: Color = Color.Blue,
  topImage: Expression<ImageValue?>? = LocationIndicatorDefaults.topImage(),
  bearingImage: Expression<ImageValue?>? = LocationIndicatorDefaults.bearingImage(),
  shadowImage: Expression<ImageValue?>? = LocationIndicatorDefaults.shadowImage(),
  topImageSize: Float = 1f,
  bearingImageSize: Float = 1f,
  shadowImageSize: Float = 1f,
  imageTiltDisplacement: Float = 0f,
  perspectiveCompensation: Float = 0.85f,
  locationTransition: TransitionOptions = TransitionOptions(),
  bearingTransition: TransitionOptions = locationTransition,
  accuracyRadiusTransition: TransitionOptions = locationTransition,
  bearingAccuracyTransition: TransitionOptions = bearingTransition,
  onClick: (ClickEvent.() -> ClickResult)? = null,
  onLongClick: (ClickEvent.() -> ClickResult)? = null,
  onDoubleClick: (ClickEvent.() -> ClickResult)? = null,
  hitPadding: Dp = 0.dp,
) {
  require(
    accuracyRadius == null || (accuracyRadius.inMeters.isFinite() && accuracyRadius.inMeters >= 0)
  ) {
    "accuracyRadius must be finite and nonnegative, was $accuracyRadius"
  }
  require(
    bearingAccuracy == null ||
      (bearingAccuracy.inDegrees.isFinite() && bearingAccuracy.inDegrees >= 0)
  ) {
    "bearingAccuracy must be finite and nonnegative, was $bearingAccuracy"
  }
  require(hitPadding.value.isFinite() && hitPadding.value >= 0f) {
    "hitPadding must be finite and nonnegative, was $hitPadding"
  }
  if (location == null) return
  key(id) {
    ComposeLocationIndicator(
      LocationIndicatorProperties(
        id = id,
        location = location,
        bearing = bearing,
        bearingAccuracy = bearingAccuracy,
        accuracyRadius = accuracyRadius,
        minZoom = minZoom,
        maxZoom = maxZoom,
        visible = visible,
        bearingAccuracyRadius = bearingAccuracyRadius,
        bearingAccuracyColor = bearingAccuracyColor,
        accuracyRadiusColor = accuracyRadiusColor,
        accuracyRadiusBorderColor = accuracyRadiusBorderColor,
        topImage = topImage,
        bearingImage = bearingImage,
        shadowImage = shadowImage,
        topImageSize = topImageSize,
        bearingImageSize = bearingImageSize,
        shadowImageSize = shadowImageSize,
        imageTiltDisplacement = imageTiltDisplacement,
        perspectiveCompensation = perspectiveCompensation,
        locationTransition = locationTransition,
        bearingTransition = bearingTransition,
        accuracyRadiusTransition = accuracyRadiusTransition,
        bearingAccuracyTransition = bearingAccuracyTransition,
        onClick = onClick,
        onLongClick = onLongClick,
        onDoubleClick = onDoubleClick,
        hitPadding = hitPadding,
      )
    )
  }
}

internal data class LocationIndicatorProperties(
  val id: String,
  val location: Position,
  val bearing: Bearing?,
  val accuracyRadius: Length?,
  val bearingAccuracy: Rotation?,
  val bearingAccuracyRadius: Expression<DpValue>,
  val bearingAccuracyColor: Expression<ColorValue>,
  val bearingAccuracyTransition: TransitionOptions,
  val minZoom: Float,
  val maxZoom: Float,
  val visible: Boolean,
  val accuracyRadiusColor: Color,
  val accuracyRadiusBorderColor: Color,
  val topImage: Expression<ImageValue?>?,
  val bearingImage: Expression<ImageValue?>?,
  val shadowImage: Expression<ImageValue?>?,
  val topImageSize: Float,
  val bearingImageSize: Float,
  val shadowImageSize: Float,
  val imageTiltDisplacement: Float,
  val perspectiveCompensation: Float,
  val locationTransition: TransitionOptions,
  val bearingTransition: TransitionOptions,
  val accuracyRadiusTransition: TransitionOptions,
  val onClick: (ClickEvent.() -> ClickResult)?,
  val onLongClick: (ClickEvent.() -> ClickResult)?,
  val onDoubleClick: (ClickEvent.() -> ClickResult)?,
  val hitPadding: Dp,
)

internal fun (ClickEvent.() -> ClickResult).asFeaturesClickHandler(): FeaturesClickHandler {
  val onClick = this
  return { onClick(this) }
}
